import React, { useContext, useEffect, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EventContext, LoaderContext, RegulationContext } from '../../../App';
import { EventType, STATUS } from '../../../core/constants/common';
import { LS_AUTHORITY_KEY } from '../../../core/constants/common';
import KPIService from '../../../core/service/KPIService';
import DataViewFilter from '../../Common/Dataview/DataViewFilter';
import ModernKpiGraphCard from './ModernKpiGraphCard';
import ModernEmptyGraphCard from './ModernEmptyGraphCard';
import ModernKpiFilter from './ModernKpiFilter';
import './GoalsAndKpisTab.scss';

const GoalsAndKpisTab = ({ isKpiFilterVisible, onToggleKpiFilter }) => {
  const { t } = useTranslation();
  const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const lsDat = JSON.parse(localStorage.getItem("authority"));
  const companyId = lsDat.profile.company.id;

  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { goalId } = useParams();

  const year = new Date().getFullYear();
  const [kpiGraph, setKPIGraph] = useState({ title: '', status: '', graphs: [] });
  const [kpiFilter, setKPIFilter] = useState({ reportingPeriod: `${year}-01-01` });
  const { event, setEvent } = useContext(EventContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (event.event !== EventType.REFRESH_COMPANY_GRAPHS) return;

    // Reset event first
    setEvent({
      event: '',
      data: '',
      status: ''
    });
    // Refresh KPI graphs if needed
  }, [event]);

  // Auto-loading is now handled by ModernKpiFilter component

  const loadKPIGraphs = (filter) => {
    setLoaderDisplay(true);
    if (!filter.kpiId && !filter.goalId) {
      setKPIGraph({ title: '', status: '', graphs: [] });
      setLoaderDisplay(false);
      return;
    }

    const period = `${new Date(filter.reportingPeriod).getFullYear()}-01-01`;
    let req;
    if (filter.kpiId > 0) {
      req = KPIService.getGraphByKPI(companyId, period, filter.kpiId);
    } else if (filter.goalId > 0) {
      req = KPIService.getGraphByGoal(companyId, period, filter.goalId);
    }
    req?.then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200) return;
      if (filter.kpiId > 0) {
        setKPIGraph({ ...kpiGraph, graphs: [res.data.data] });
      } else if (filter.goalId > 0) {
        setKPIGraph(res.data.data ?? { graphs: [] });
      }
    }).catch(err => {
      setLoaderDisplay(false);
    });
  };

  const renderNavigateViewButtons = () => {
    return (
      <div className="modern-kpi-toggle-buttons">
        <div className="toggle-button-group">
          <Form.Check
            className="toggle-switch"
            inline
            label={t("Status.label")}
            name="assessType"
            type="radio"
            id="statusView"
            onChange={(e) => toggleViews(e.target.id)}
          />
          <Form.Check
            className="toggle-switch"
            inline
            label={t("Analytical.label")}
            name="assessType"
            type="radio"
            id="analyticalView"
            checked
          />
          <Form.Check
            className="toggle-switch"
            inline
            label={t("Data.label")}
            name="assessType"
            type="radio"
            id="dataView"
            onChange={(e) => toggleViews(e.target.id)}
          />
        </div>
      </div>
    );
  };

  const toggleViews = (view) => {
    if (view === 'dataView') {
      navigate(`/company_admin/kpidataview/goal/${goalId}`, { state: { goal: location.state?.goal } });
    } else if (view === 'statusView') {
      navigate(`/company_admin/kpistatusview/goal/${goalId}`, { state: { goal: location.state?.goal } });
    }
  };

  const renderKPIGraphs = () => {
    return (
      <>
        <div className="modern-kpi-header">
          <h6 className="modern-kpi-title">{kpiGraph.title}</h6>
          {kpiGraph.status && (
            <Badge
              bg={kpiGraph.status === 'COMPLETED' ? 'success' : kpiGraph.status === 'IN_PROGRESS' ? 'warning' : 'secondary'}
              className="modern-kpi-status-badge"
            >
              {STATUS[kpiGraph.status]}
            </Badge>
          )}
        </div>
        <div className="modern-kpi-graphs-grid row m-0">
          {kpiGraph?.graphs?.map((graph, index) => (
            <div className="col-12 col-md-6 col-lg-6 p-2" key={`modernKpiGraph-${index}`}>
              <div className="modern-kpi-graph-item p-0">
                <ModernKpiGraphCard graphData={graph} />
              </div>
            </div>
          ))}
          {(!kpiGraph?.graphs || kpiGraph?.graphs?.length === 0) && (
            <ModernEmptyGraphCard graphConfig={true} />
          )}
        </div>
      </>
    );
  };

  return (
    <div className="modern-goals-kpis-tab">
      {goalId && renderNavigateViewButtons()}

      <div className="modern-kpi-filter-section"
        style={{ display: isKpiFilterVisible ? 'block' : 'none' }}>
        {/* Filter panel - controlled by parent component */}

        <ModernKpiFilter
          onApplyFilter={(f) => loadKPIGraphs(f)}
          onFilterHide={(value) => onToggleKpiFilter && onToggleKpiFilter()}
          goalId={goalId}
          onLoad={(d) => {
            // Auto-load graphs when component mounts or reset is clicked
            if (d.type === 'GOALS' || d.type === 'RESET') {
              loadKPIGraphs(d.filter);
            }
          }}
        />
      </div>

      <div className="modern-kpi-content">
        {renderKPIGraphs()}
      </div>
    </div>
  );
};

export default GoalsAndKpisTab; 